import MenuIcon from "@mui/icons-material/Menu";
import { AppBar, Avatar, Box, Chip, Divider, IconButton, ListItemIcon, Menu, MenuItem, Toolbar, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import LockResetIcon from '@mui/icons-material/LockReset'; // For reset password icon
import LogoutIcon from '@mui/icons-material/Logout'; // For logout icon
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'; // Arrow icon for dropdown

import VpnKeyIcon from '@mui/icons-material/VpnKey'; // Change password icon
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { UserRole } from "../../auth/roles";
import { NAVBAR_HEIGHT, navbarSurfaceSx } from "../common/layoutConstants";
import { BrandLogo } from "../common/BrandLogo";
import { getLoggedInUserName } from "../../modules/common/user/UserService";
import { useAcademicYears } from "../../features/academicOperations/hooks/useAcademicYears";
import { AcademicYear } from "../../features/academicOperations/types";
import { useAcademicYearContext } from "../../context/AacademicYearContext";

const ROLE_LABELS: Record<string, string> = {
  [UserRole.ADMIN]: "Admin",
  [UserRole.FACULTY]: "Faculty",
  [UserRole.STUDENT]: "Student",
  [UserRole.OPERATOR]: "Operator",
};

/** "Abdul Mateen" -> "AM", "admin" -> "A" */
const initialsOf = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join("") || "?";

/** The real name saved at login (userDTO.name); right after login AuthContext only has the typed login id. */
const storedName = (): string | null => {
  try {
    const name = getLoggedInUserName();
    return name && name !== "null" && name !== "undefined" ? name : null;
  } catch {
    return null;
  }
};

export const AppTopNavbar = ({ onToggleSidebar }: { onToggleSidebar: () => void }) => {
  const { user, loaded: authLoaded } = useAuth();
  const displayName = storedName() ?? user?.name ?? "";
  console.log("Entering AppTopNavbar");
  const { academicYears, setCurrentAcademicYear, loaded: academicYearLoaded } = useAcademicYearContext();
  console.log("academicYears " + academicYears);
  const navigate = useNavigate();

  useEffect(() => {
  }, []);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);

  // Handle opening the menu
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  // Handle closing the menu
  const handleMenuClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  // Handle menu actions
  const handleResetPassword = () => {
    console.log('Reset password clicked');
    handleMenuClose();
    // Add your reset password logic here
    navigate('/app/account/password');
  };
  const handleLogout = () => {
    console.log('Logout clicked');
    handleMenuClose();
    // Add your logout logic here
    navigate('/logout');
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        ...navbarSurfaceSx,
        zIndex: (theme) => theme.zIndex.drawer + 1
      }}
    >
      <Toolbar sx={{ height: NAVBAR_HEIGHT, gap: 1, px: { xs: 2, md: 3 } }}>
        <IconButton onClick={onToggleSidebar} edge="start" aria-label="Toggle menu" sx={{ color: "#111" }}>
          <MenuIcon />
        </IconButton>
        <BrandLogo to="/app/dashboard" />

        <Box sx={{ flexGrow: 1 }} />

        {/* Academic Year selector — admin only */}

        <IconButton onClick={handleMenuClick} sx={{ p: 0.5 }} aria-label="account menu">
          <Avatar sx={{ width: 34, height: 34, fontSize: 14, fontWeight: 600, bgcolor: "primary.main" }}>
            {initialsOf(displayName)}
          </Avatar>
        </IconButton>

        {/* Account Menu */}
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              elevation: 0,
              sx: {
                mt: 1,
                width: 260,
                borderRadius: 3,
                overflow: "visible",
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 12px 32px rgba(15, 23, 42, 0.12)",
                // small arrow pointing at the avatar
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: -6,
                  right: 18,
                  width: 12,
                  height: 12,
                  bgcolor: "background.paper",
                  borderLeft: "1px solid",
                  borderTop: "1px solid",
                  borderColor: "divider",
                  transform: "rotate(45deg)",
                },
              },
            },
            list: { sx: { py: 0 } },
          }}
        >
          {/* who is logged in */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2, py: 2 }}>
            <Avatar sx={{ width: 44, height: 44, fontSize: 16, fontWeight: 600, bgcolor: "primary.main" }}>
              {initialsOf(displayName)}
            </Avatar>
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={600} lineHeight={1.3} noWrap title={displayName}>
                {displayName}
              </Typography>
              {ROLE_LABELS[user?.role ?? ""] && (
                <Chip
                  label={ROLE_LABELS[user?.role ?? ""]}
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mt: 0.5, height: 20, fontSize: 11, fontWeight: 600 }}
                />
              )}
            </Box>
          </Box>
          <Divider />

          <Box sx={{ p: 1 }}>
            <MenuItem onClick={handleResetPassword} sx={{ borderRadius: 2, py: 1 }}>
              <ListItemIcon>
                <VpnKeyIcon fontSize="small" />
              </ListItemIcon>
              Change Password
            </MenuItem>
            <MenuItem onClick={handleLogout} sx={{ borderRadius: 2, py: 1, color: "error.main" }}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" sx={{ color: "error.main" }} />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Box>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};
